import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bell, ArrowDownToLine, ArrowUpFromLine, Landmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useNotificationsStore,
  type FinanceNotification,
} from '@/stores/notifications-store';
import { formatMoneyMinor, shortId } from '@/lib/finance-format';

function formatRelative(t: ReturnType<typeof useTranslation>['t'], ms: number): string {
  const sec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (sec < 60) return t('time.justNow');
  const min = Math.floor(sec / 60);
  if (min < 60) return t('time.minutesAgo', { count: min });
  const hr = Math.floor(min / 60);
  return t('time.hoursAgo', { count: hr });
}

function titleFor(
  t: ReturnType<typeof useTranslation>['t'],
  kind: FinanceNotification['kind'],
): string {
  switch (kind) {
    case 'deposit.created': return t('finance.notifications.depositCreated');
    case 'withdraw.created': return t('finance.notifications.withdrawCreated');
    case 'deposit.updated': return t('finance.notifications.depositUpdated');
    case 'withdraw.updated': return t('finance.notifications.withdrawUpdated');
    case 'bank_account.created': return t('finance.notifications.bankAccountCreated');
    case 'bank_account.updated': return t('finance.notifications.bankAccountReverify');
  }
}

function statusBadgeClass(status: string | undefined): string {
  switch (status) {
    case 'approved':
    case 'paid':
    case 'completed':
      return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
    case 'rejected':
    case 'cancelled':
      return 'bg-rose-500/15 text-rose-400 border-rose-500/30';
    case 'verifying':
      return 'bg-blue-500/15 text-blue-400 border-blue-500/30';
    case 'pending':
    default:
      return 'bg-amber-500/15 text-amber-400 border-amber-500/30';
  }
}

export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const events = useNotificationsStore((s) => s.events);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);

  const handleSelect = (n: FinanceNotification) => {
    const tab = n.kind.startsWith('bank_account')
      ? 'member-banks'
      : n.kind.startsWith('deposit')
        ? 'deposits'
        : 'withdraws';
    markAllRead();
    navigate(`/finance?tab=${tab}`);
  };

  return (
    <DropdownMenu onOpenChange={(open) => { if (open) markAllRead(); }}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('finance.notifications.title')}
          className="relative"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground"
              data-testid="notification-badge"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="text-sm font-semibold">{t('finance.notifications.title')}</span>
          {events.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {events.length}
            </span>
          )}
        </div>
        <div className="max-h-[28rem] overflow-y-auto">
          {events.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              {t('finance.notifications.empty')}
            </div>
          ) : (
            events.map((n, i) => {
              const isBank = n.kind.startsWith('bank_account');
              const isDeposit = n.kind.startsWith('deposit');
              const Icon = isBank ? Landmark : isDeposit ? ArrowDownToLine : ArrowUpFromLine;
              const iconColor = isBank
                ? 'text-violet-400'
                : isDeposit
                  ? 'text-emerald-400'
                  : 'text-amber-400';
              const amount = isBank ? undefined : formatMoneyMinor(n.amount, 'THB');
              const maskedAccount = isBank && n.accountNumber
                ? `****${n.accountNumber.slice(-4)}`
                : undefined;
              return (
                <button
                  key={`${n.id ?? 'no-id'}-${n.receivedAt}-${i}`}
                  type="button"
                  onClick={() => handleSelect(n)}
                  className="flex w-full items-start gap-3 border-b px-4 py-3 text-left hover:bg-accent"
                >
                  <span className={`mt-0.5 shrink-0 rounded-md bg-muted p-1.5 ${iconColor}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="flex-1 min-w-0 flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold truncate">
                        {titleFor(t, n.kind)}
                      </span>
                      {amount && (
                        <span className="text-sm font-bold tabular-nums shrink-0">
                          {amount}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {isBank ? (
                        <>
                          {n.username && (
                            <span className="text-xs text-muted-foreground">{n.username}</span>
                          )}
                          {maskedAccount && (
                            <span className="text-xs font-mono text-muted-foreground">
                              {maskedAccount}
                            </span>
                          )}
                          <span
                            className="inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide bg-amber-500/15 text-amber-400 border-amber-500/30"
                          >
                            {t('members.bankUnverified')}
                          </span>
                        </>
                      ) : (
                        <>
                          {n.status && (
                            <span
                              className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${statusBadgeClass(n.status)}`}
                            >
                              {t(`finance.status.${n.status}`, n.status)}
                            </span>
                          )}
                          {n.memberId && (
                            <span className="text-xs text-muted-foreground">
                              {t('finance.notifications.member')} {shortId(n.memberId)}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                      {n.id && <span className="font-mono">#{shortId(n.id)}</span>}
                      <span>{formatRelative(t, n.receivedAt)}</span>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
